import { useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { addToast } from 'components/Toast/Toast'

import placeholderAvatar from 'assets/images/user_placeholder.png'
import {
  IconChatDashed,
  IconSendOutline,
  IconVolumeFill,
  IconVolumeMutedFill,
} from 'assets/svg/sprite'
import { ChatOptionsDropdown } from 'components-ref/molecules/post/OptionsDropdown/ChatOptionsDropdown'
import { useLongPress } from 'helpers/hooks/useLongPress'
import {
  devTapMessage,
  fetchChatHistory,
  kickStreamUser,
  // deleteChatMessage,
  // reportChatMessage,
} from 'services/endpoints/streaming'
import { useLiveChatPusher } from 'services/streaming/useLiveChatPusher'
import { useAccessToken, useUserProfile } from 'store'
import { useStreamingStore } from 'store/streaming'
import { useIvsChatStore } from 'store/useIvsChatStore'
import './_liveChat.scss'

declare global {
  interface Window {
    __IVS_CHAT__?: {
      region?: string
      tokenProvider: () => Promise<{
        token: string
        sessionExpirationTime?: Date | string
        tokenExpirationTime?: Date | string
      }>
      roomArn?: string
    }
  }
}

type LiveChatProps = {
  visible: boolean
  disabled?: boolean
  streamOwnerId?: string | null
  streamId?: string | null
  chatReady?: boolean
}

type ChatMessageLike = {
  id?: string
  content?: string
  username?: string
  avatar_url?: string
  to_user?: string | number | null
  sender?: {
    userId?: string | number
  }
  attributes?: unknown
  isOwner?: boolean
  user_id?: string
  sender_id?: number | string
}

type TargetUser = {
  id: string
  username?: string
  avatar_url?: string
}

type LiveChatDropdownMode = 'message' | 'kick'

type LiveChatMessageItemProps = {
  message: ChatMessageLike
  currentUserId?: string | number | null
  canKick?: boolean
  onOpenMessageOptions: (message: ChatMessageLike) => void
  onSelectTargetUser: (target: TargetUser) => void
  onOpenKickOptions: (target: TargetUser, message: ChatMessageLike) => void
}

const parseMessageAttrs = (message: ChatMessageLike): Record<string, any> => {
  let parsedMessageAttrs: Record<string, any> = {}

  try {
    const rawAttrs = (message as any)?.attributes

    if (typeof rawAttrs === 'string') {
      parsedMessageAttrs = JSON.parse(rawAttrs)
    } else if (rawAttrs && typeof rawAttrs === 'object') {
      parsedMessageAttrs =
        typeof rawAttrs.attributes === 'string'
          ? JSON.parse(rawAttrs.attributes)
          : (rawAttrs as Record<string, any>)
    }
  } catch {
    parsedMessageAttrs = {}
  }

  return parsedMessageAttrs
}

const LiveChatMessageItem = ({
  message,
  currentUserId,
  canKick = false,
  onOpenMessageOptions,
  onSelectTargetUser,
  onOpenKickOptions,
}: LiveChatMessageItemProps) => {
  const parsedMessageAttrs = parseMessageAttrs(message)

  const senderId =
    (message as any)?.user_id ??
    (message as any)?.sender_id ??
    message.sender?.userId ??
    parsedMessageAttrs.user_id ??
    parsedMessageAttrs.sender_id ??
    null

  const name =
    (message as any)?.username ??
    parsedMessageAttrs.username ??
    message.sender?.userId ??
    'User'

  const avatar =
    (message as any)?.avatar_url ??
    parsedMessageAttrs.avatar ??
    placeholderAvatar

  const text = message.content ?? ''
  const highlighted = (message as any)?.isOwner === true
  const isTip = (message as any)?.isTip === true
  const isJoin = (message as any)?.isJoin === true
  const hideName = isTip || isJoin

  const handleSelectUser = () => {
    if (!senderId) return
    if (String(senderId) === String(currentUserId ?? '')) return

    onSelectTargetUser({
      id: String(senderId),
      username: typeof name === 'string' ? name : undefined,
      avatar_url: typeof avatar === 'string' ? avatar : undefined,
    })
  }

  const contentLongPressHandlers = useLongPress({
    delay: 300,
    moveThreshold: 20,
    onLongPress: () => {
      if ((message as any)?.isTip) return
      onOpenMessageOptions(message)
    },
    onClick: () => {
      handleSelectUser()
    },
  })

  const avatarLongPressHandlers = useLongPress({
    delay: 300,
    moveThreshold: 20,
    onLongPress: () => {
      if (!canKick) return
      if (!senderId) return
      if (String(senderId) === String(currentUserId ?? '')) return

      onOpenKickOptions(
        {
          id: String(senderId),
          username: typeof name === 'string' ? name : undefined,
          avatar_url: typeof avatar === 'string' ? avatar : undefined,
        },
        message
      )
    },
    onClick: () => {
      handleSelectUser()
    },
  })

  return (
    <div className={`livechat__message${highlighted ? ' highlighted' : ''}`}>
      <button
        type='button'
        className='livechat__message__avatar livechat__message__avatar--button'
        onClick={handleSelectUser}
        {...avatarLongPressHandlers}
      >
        <img
          src={avatar}
          alt=''
          onError={e => {
            e.currentTarget.src = placeholderAvatar
          }}
        />
      </button>

      <div
        className={`livechat__message__content ${
          isTip ? 'livechat__message__content--tip' : ''
        }`}
        onClick={handleSelectUser}
        {...contentLongPressHandlers}
      >
        {!hideName && (
          <div className='livechat__message__content--name'>{name}</div>
        )}

        <div className='livechat__message__content--text'>{text}</div>
      </div>
    </div>
  )
}

const LiveChat = ({
  visible,
  disabled = false,
  streamOwnerId,
  streamId,
  chatReady = false,
}: LiveChatProps) => {
  const { t } = useTranslation()
  const userData = useUserProfile()
  const muted = useStreamingStore(s => s.muted)

  const streamKey = String(streamId ?? '')
  const token = useAccessToken()

  const messages = useIvsChatStore(s => s.messagesByStream[streamKey] ?? [])
  const connection = useIvsChatStore(s => s.connection)
  const isSending = useIvsChatStore(s => s.isSending)
  const historyLoaded = useIvsChatStore(
    s => s.historyLoadedByStream[streamKey] ?? false
  )

  const sendMessage = useIvsChatStore(s => s.sendMessage)
  const connectChat = useIvsChatStore(s => s.connectChat)
  const disconnectChat = useIvsChatStore(s => s.disconnectChat)
  const markHistoryLoadedForStream = useIvsChatStore(
    s => s.markHistoryLoadedForStream
  )
  const clearHistoryLoadedForStream = useIvsChatStore(
    s => s.clearHistoryLoadedForStream
  )
  const prependHistoryForStream = useIvsChatStore(
    s => s.prependHistoryForStream
  )

  const [newChatMessage, setNewChatMessage] = useState('')
  const [isChatOptionsOpen, setIsChatOptionsOpen] = useState(false)
  const [selectedMessage, setSelectedMessage] = useState<ChatMessageLike | null>(
    null
  )
  const [selectedKickTarget, setSelectedKickTarget] = useState<TargetUser | null>(
    null
  )
  const [dropdownMode, setDropdownMode] =
    useState<LiveChatDropdownMode>('message')
  const [targetUser, setTargetUser] = useState<TargetUser | null>(null)

  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const previousStreamKeyRef = useRef(streamKey)
  const hasShownNotReadyToastRef = useRef(false)

  const currentUserId = (userData as any)?.id ?? null

  useLiveChatPusher({
    token,
    currentUserId,
    streamId,
  })

  const isStreamOwner =
    String(streamOwnerId ?? '') !== '' &&
    String(streamOwnerId) === String(currentUserId ?? '')

  const hasChatConfig =
    !!window.__IVS_CHAT__?.tokenProvider && !!window.__IVS_CHAT__?.roomArn

  // Only creators are gated by delayed chat bootstrap.
  const ownerChatBootstrapReady = !isStreamOwner || chatReady

  const getResolvedRegion = () => window.__IVS_CHAT__?.region ?? 'us-west-2'

  const getResolvedTokenProvider = () => {
    const provider = window.__IVS_CHAT__?.tokenProvider
    if (!provider) {
      throw new Error('IVS token provider missing')
    }
    return provider
  }

  const devTapWithStreamId = useMemo(() => {
    return async (payload: any) => {
      return await devTapMessage({
        ...payload,
        stream_id: streamId ?? null,
      })
    }
  }, [streamId])

  const inputDisabled = isSending

  const showChatNotReadyToast = () => {
    if (hasShownNotReadyToastRef.current) return
    hasShownNotReadyToastRef.current = true

    addToast('error', 'Chat not ready')

    window.setTimeout(() => {
      hasShownNotReadyToastRef.current = false
    }, 1200)
  }

  const handleKickUser = async () => {
    if (!streamId) return
    if (!selectedKickTarget?.id) return
    if (!isStreamOwner) return

    await kickStreamUser({
      stream_id: streamId,
      user_id: selectedKickTarget.id,
    })

    setIsChatOptionsOpen(false)
    setSelectedKickTarget(null)
    setSelectedMessage(null)
  }

  const handleOpenKickOptions = (
    target: TargetUser,
    message: ChatMessageLike
  ) => {
    if (!isStreamOwner) return
    if (!streamId) return

    setSelectedKickTarget(target)
    setSelectedMessage(message)
    setDropdownMode('kick')
    setIsChatOptionsOpen(true)
  }

  const handleOpenMessageOptions = (message: ChatMessageLike) => {
    setSelectedMessage(message)
    setSelectedKickTarget(null)
    setDropdownMode('message')
    setIsChatOptionsOpen(true)
  }

  const handleCloseMessageOptions = (value: boolean) => {
    setIsChatOptionsOpen(value)

    if (!value) {
      setSelectedMessage(null)
      setSelectedKickTarget(null)
      setDropdownMode('message')
    }
  }

  const handleSelectTargetUser = (target: TargetUser) => {
    if (String(target.id) === String(currentUserId ?? '')) return
    setTargetUser(target)
  }

  const handleClearTargetUser = () => {
    setTargetUser(null)
  }

  const selectedMessageRole: 'owner' | 'user' = (() => {
    if (!selectedMessage) return 'user'

    const selectedMessageUserId =
      (selectedMessage as any)?.user_id ??
      (selectedMessage as any)?.sender_id ??
      (selectedMessage as any)?.sender?.userId ??
      ''

    if (String(selectedMessageUserId) === String(currentUserId ?? '')) {
      return 'owner'
    }

    if ((selectedMessage as any)?.isOwner === true) {
      return 'owner'
    }

    return 'user'
  })()

const handleDeleteMessage = async (_messageId: string) => {
  setIsChatOptionsOpen(false)
  setSelectedMessage(null)
  setSelectedKickTarget(null)
}

const handleReportMessage = async ({
  messageId,
  message,
  reportType,
}: {
  messageId: string
  message: string
  reportType: number
}) => {
  // implementation later
}

  const handleSend = async () => {
    const trimmed = newChatMessage.trim()
    if (!trimmed) return
    if (isSending) return

    if (!ownerChatBootstrapReady || !hasChatConfig || connection !== 'connected') {
      showChatNotReadyToast()
      return
    }

    try {
      await sendMessage(trimmed, {
        toUser: targetUser?.id ?? null,
      })

      setNewChatMessage('')
    } catch {
      showChatNotReadyToast()
    }
  }

  useEffect(() => {
    return () => {
      disconnectChat()
    }
  }, [disconnectChat])

  useEffect(() => {
    const previousStreamKey = previousStreamKeyRef.current
    const streamChanged = previousStreamKey !== streamKey

    if (streamChanged) {
      disconnectChat()
      previousStreamKeyRef.current = streamKey
      setTargetUser(null)
      setSelectedMessage(null)
      setSelectedKickTarget(null)
      setDropdownMode('message')
      setIsChatOptionsOpen(false)
      hasShownNotReadyToastRef.current = false
    }
  }, [streamKey, disconnectChat])

  useEffect(() => {
    if (!streamKey) return
    if (!ownerChatBootstrapReady) return
    if (!window.__IVS_CHAT__?.tokenProvider) return
    if (!window.__IVS_CHAT__?.roomArn) return

    if (connection !== 'idle' && connection !== 'disconnected') {
      return
    }

    void connectChat({
      region: getResolvedRegion(),
      tokenProvider: getResolvedTokenProvider(),
      archiver: true,
      userData: userData as any,
      devTap: devTapWithStreamId,
      roomArn: window.__IVS_CHAT__?.roomArn,
      streamId,
      streamOwnerId,
    })
  }, [
    streamKey,
    streamId,
    connection,
    connectChat,
    userData,
    devTapWithStreamId,
    streamOwnerId,
    ownerChatBootstrapReady,
  ])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamKey])

  useEffect(() => {
    if (!streamKey) return
    if (!ownerChatBootstrapReady) return
    if (!window.__IVS_CHAT__?.roomArn) return
    if (isStreamOwner) return
    if (historyLoaded) return

    markHistoryLoadedForStream(streamKey)

    void (async () => {
      try {
        const rows = await fetchChatHistory({
          roomArn: window.__IVS_CHAT__!.roomArn!,
          stream_id: streamId ?? null,
          limit: 50,
        })

        if (Array.isArray(rows) && rows.length > 0) {
          prependHistoryForStream(streamKey, rows, streamOwnerId)
        }
      } catch {
        clearHistoryLoadedForStream(streamKey)
      }
    })()
  }, [
    streamKey,
    isStreamOwner,
    historyLoaded,
    streamOwnerId,
    streamId,
    markHistoryLoadedForStream,
    clearHistoryLoadedForStream,
    prependHistoryForStream,
    ownerChatBootstrapReady,
  ])

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      void handleSend()
    }
  }

  return (
    <>
      <div
        className={`livechat${visible ? '' : ' livechat--hidden'}${
          disabled ? ' livechat--disabled' : ''
        }`}
      >
        <div className='livechat__messages'>
          {messages.map((message, idx) => (
            <LiveChatMessageItem
              key={(message as any)?.id ?? idx}
              message={message as ChatMessageLike}
              currentUserId={currentUserId}
              canKick={isStreamOwner}
              onOpenMessageOptions={handleOpenMessageOptions}
              onSelectTargetUser={handleSelectTargetUser}
              onOpenKickOptions={handleOpenKickOptions}
            />
          ))}

          <div ref={messagesEndRef} />
        </div>

        <div className='livechat__footer'>
          <div className='livechat__footer--left'>
            <div className='livechat__footer--icon'>
              {muted ? <IconVolumeMutedFill /> : <IconVolumeFill />}
            </div>
          </div>

          <div className='livechat__footer--right'>
            <div className='livechat__footer__input'>
              <div className='livechat__footer__input--icon'>
                {targetUser?.avatar_url ? (
                  <button
                    type='button'
                    className='livechat__targetUser'
                    onClick={handleClearTargetUser}
                    title={
                      targetUser.username
                        ? `Sending to ${targetUser.username}. Click to clear.`
                        : 'Clear selected recipient'
                    }
                  >
                    <img
                      src={targetUser.avatar_url}
                      alt=''
                      onError={e => {
                        e.currentTarget.src = placeholderAvatar
                      }}
                    />
                  </button>
                ) : (
                  <IconChatDashed />
                )}
              </div>

              <input
                type='text'
                onChange={e => {
                  setNewChatMessage(e.currentTarget.value)
                }}
                onKeyDown={onKeyDown}
                value={newChatMessage}
                placeholder={
                  targetUser?.username
                    ? `Message ${targetUser.username}`
                    : t('commentHere')
                }
                disabled={inputDisabled}
              />
            </div>

            <div
              className='livechat__footer__send'
              onClick={() => {
                void handleSend()
              }}
            >
              <div className='livechat__footer__send--icon'>
                <IconSendOutline />
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedMessage && (
        <ChatOptionsDropdown
          isOpen={isChatOptionsOpen}
          setIsOpen={handleCloseMessageOptions}
          messageId={selectedMessage.id ?? ''}
          role={selectedMessageRole}
          mode={dropdownMode}
          kickLabel={
            selectedKickTarget?.username
              ? `Kick ${selectedKickTarget.username}`
              : 'Kick user'
          }
          kickUserFn={handleKickUser}
          withoutBottomNavigation
          deleteMessageFn={handleDeleteMessage}
          reportMessageFn={handleReportMessage}
        />
      )}
    </>
  )
}

export default LiveChat